import { X } from "lucide-react";
import { clsx } from "clsx";
import { Dialog } from "radix-ui";
import React, { PropsWithChildren } from "react";

import { twMerge } from "@/design-system/twMerge";

export type DrawerAccent = "none" | "critical" | "warning" | "success" | "info";

const accentBarClasses: Record<Exclude<DrawerAccent, "none">, string> = {
  critical: "bg-status-error",
  warning: "bg-status-warning",
  success: "bg-status-success",
  info: "bg-status-info",
};

interface DrawerProps {
  title: string;
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Icon shown in a 30×30 tile to the left of the title (compact headers). */
  titleLeading?: React.ReactNode;
  /** Tighter header padding and title size for chat-style panels. */
  headerDensity?: "default" | "compact";
  accent?: DrawerAccent;
  accentColor?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  children: React.ReactNode;
  placement: "bottom" | "right";
  className?: string;
  titleClassName?: string;
  overlayProps?: Partial<Dialog.DialogOverlayProps>;
}

export function Drawer({
  title,
  eyebrow,
  subtitle,
  titleLeading,
  headerDensity = "default",
  accent = "none",
  accentColor,
  children,
  onOpenChange,
  placement,
  className,
  titleClassName,
  overlayProps,
  ...rest
}: DrawerProps) {
  return (
    <Dialog.Root {...rest}>
      <Dialog.Portal>
        <Dialog.Overlay
          {...overlayProps}
          className={twMerge(
            clsx(
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed top-0 left-0 flex h-full w-full flex-col bg-black/50 pt-12",
              overlayProps?.className,
            ),
          )}
        >
          <Dialog.Content
            onEscapeKeyDown={() => onOpenChange(false)}
            onPointerDownOutside={() => onOpenChange(false)}
            className={clsx(
              "bg-surface-default flex min-h-0 flex-1 flex-col overflow-hidden",
              placement === "bottom" &&
                "data-[state=open]:animate-slideInUp data-[state=closed]:animate-slideOutDown rounded-t-lg",
              placement === "right" &&
                "data-[state=open]:animate-slideInFromRight data-[state=closed]:animate-slideOutToRight fixed top-0 right-0 h-full w-96 max-w-[90vw]",
              className,
            )}
          >
            <div
              className={clsx(
                headerDensity === "compact"
                  ? "flex flex-0 items-center justify-between gap-2.5 border-b px-5 py-[18px]"
                  : "border-borderSubtle flex flex-0 justify-between gap-4 border-b px-8 py-6",
                headerDensity !== "compact" &&
                  (eyebrow || subtitle ? "items-start" : "items-center"),
                titleClassName,
              )}
            >
              <div
                className={clsx(
                  "flex min-w-0 flex-1",
                  headerDensity === "compact"
                    ? "items-center gap-2.5"
                    : "items-stretch gap-4",
                )}
              >
                {(accent !== "none" || accentColor) && (
                  <div
                    className={clsx(
                      "w-1 shrink-0 self-stretch",
                      !accentColor &&
                        accent !== "none" &&
                        accentBarClasses[accent],
                    )}
                    style={
                      accentColor ? { backgroundColor: accentColor } : undefined
                    }
                    aria-hidden="true"
                  />
                )}
                {titleLeading && (
                  <div
                    className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-white/12"
                    aria-hidden
                  >
                    {titleLeading}
                  </div>
                )}
                <div className="flex min-w-0 flex-col gap-0.5">
                  {eyebrow && (
                    <span className="text-eyebrow-xs text-textSubtle truncate">
                      {eyebrow}
                    </span>
                  )}
                  <Dialog.Title
                    className={clsx(
                      headerDensity === "compact"
                        ? "text-textDefault text-[15px] leading-5 font-semibold"
                        : "text-heading-xl text-textDefault",
                    )}
                  >
                    {title}
                  </Dialog.Title>
                  {subtitle && (
                    <span
                      className={clsx(
                        "truncate",
                        headerDensity === "compact"
                          ? "text-textSubtle text-xs"
                          : "text-body-sm text-textSubtle",
                      )}
                    >
                      {subtitle}
                    </span>
                  )}
                </div>
              </div>
              <Dialog.Close
                onClick={() => onOpenChange(false)}
                className={clsx(
                  "shrink-0 cursor-pointer transition-colors",
                  headerDensity === "compact"
                    ? "text-textSubtle hover:text-textDefault flex h-7 w-7 items-center justify-center rounded-md"
                    : "text-textSubtle hover:text-textDefault",
                )}
              >
                <X
                  className={
                    headerDensity === "compact" ? "h-4 w-4" : undefined
                  }
                />
              </Dialog.Close>
            </div>
            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DrawerContent({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("min-h-0 flex-1 overflow-auto px-8 py-10", className),
      )}
    >
      {children}
    </div>
  );
}

export function DrawerCtaSection({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("border-borderSubtle flex-0 border-t px-8 py-4", className),
      )}
    >
      {children}
    </div>
  );
}
